import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

interface Employment {
  id: string;
  companyName: string;
  position: string;
  employmentType: string;
  startDate: string;
  endDate: string;
  isCurrentJob: boolean;
  location: string;
  description: string;
  reasonForLeaving?: string;
}

interface EmploymentExperienceProps {
  data: Employment[];
  onChange: (data: Employment[]) => void;
}

const employmentTypes = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'internship', label: 'Internship' },
];

const EmploymentExperience: React.FC<EmploymentExperienceProps> = ({ data, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState<Employment | null>(null);
  const [formData, setFormData] = useState<Partial<Employment>>({});

  const handleAdd = () => {
    setEditingEmployment(null);
    setFormData({ 
      employmentType: 'full_time',
      isCurrentJob: false 
    });
    setDialogOpen(true);
  };

  const handleEdit = (employment: Employment) => {
    setEditingEmployment(employment);
    setFormData(employment);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    onChange(data.filter(employment => employment.id !== id));
  };

  const handleSave = () => {
    if (!formData.companyName || !formData.position || !formData.startDate) {
      return;
    }

    const employmentData: Employment = {
      id: editingEmployment?.id || Date.now().toString(),
      companyName: formData.companyName!,
      position: formData.position!,
      employmentType: formData.employmentType || 'full_time',
      startDate: formData.startDate!,
      endDate: formData.endDate || '',
      isCurrentJob: formData.isCurrentJob || false,
      location: formData.location || '',
      description: formData.description || '',
      reasonForLeaving: formData.reasonForLeaving || '',
    };

    if (editingEmployment) {
      onChange(data.map(employment => employment.id === editingEmployment.id ? employmentData : employment));
    } else {
      onChange([...data, employmentData]);
    }

    setDialogOpen(false);
    setFormData({});
    setEditingEmployment(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setFormData({});
    setEditingEmployment(null);
  };

  const updateFormData = (field: keyof Employment, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  const calculateDuration = (startDate: string, endDate: string, isCurrentJob: boolean) => {
    const start = new Date(startDate);
    const end = isCurrentJob ? new Date() : new Date(endDate);
    
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Add any work experience you have, including driving and non-driving positions
      </Typography>

      <Stack spacing={3} mt={3}>
        {/* Add Employment Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            p: 2,
            borderStyle: 'dashed',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            }
          }}
        >
          Add Experience
        </Button>

        {/* Employment Cards */}
        {data.map((employment) => (
          <Card key={employment.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={2} flex={1}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: '#fff3e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WorkIcon sx={{ color: '#f57c00' }} />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {employment.position}
                    </Typography>
                    <Typography variant="body1" color="text.primary" gutterBottom>
                      {employment.companyName}
                    </Typography>
                    
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(employment.startDate)} - {
                            employment.isCurrentJob ? 'Present' : formatDate(employment.endDate)
                          }
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        ({calculateDuration(employment.startDate, employment.endDate, employment.isCurrentJob)})
                      </Typography>
                    </Stack>

                    {employment.location && (
                      <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {employment.location}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                      <Chip
                        label={employmentTypes.find(type => type.value === employment.employmentType)?.label}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {employment.isCurrentJob && (
                        <Chip
                          label="Current"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      )}
                    </Stack>

                    {employment.description && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {employment.description}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => handleEdit(employment)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(employment.id)} 
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}

        {data.length === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <WorkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No work experience added yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add any work experience you have, including driving and non-driving positions
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Add/Edit Employment Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingEmployment ? 'Edit Employment' : 'Add Work Experience'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Company Name</FormLabel>
                <TextField
                  value={formData.companyName || ''}
                  onChange={(e) => updateFormData('companyName', e.target.value)}
                  placeholder="e.g., ABC Transport Inc."
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Position/Job Title</FormLabel>
                <TextField
                  value={formData.position || ''}
                  onChange={(e) => updateFormData('position', e.target.value)}
                  placeholder="e.g., Long Haul Truck Driver"
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Employment Type</FormLabel>
                <Select
                  value={formData.employmentType || 'full_time'}
                  onChange={(e) => updateFormData('employmentType', e.target.value)}
                >
                  {employmentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Location</FormLabel>
                <TextField
                  value={formData.location || ''}
                  onChange={(e) => updateFormData('location', e.target.value)}
                  placeholder="e.g., Toronto, ON"
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Start Date</FormLabel>
                <TextField
                  type="date"
                  value={formData.startDate || ''}
                  onChange={(e) => updateFormData('startDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isCurrentJob || false}
                      onChange={(e) => updateFormData('isCurrentJob', e.target.checked)}
                    />
                  }
                  label="This is my current job"
                  sx={{ mb: 1 }}
                />
                {!formData.isCurrentJob && (
                  <>
                    <FormLabel sx={{ mb: 1 }}>End Date</FormLabel>
                    <TextField
                      type="date"
                      value={formData.endDate || ''}
                      onChange={(e) => updateFormData('endDate', e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Job Description</FormLabel>
                <TextField
                  value={formData.description || ''}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  placeholder="Describe your responsibilities and achievements..."
                  multiline
                  rows={3}
                  fullWidth
                />
              </FormControl>
            </Grid>

            {!formData.isCurrentJob && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1 }}>Reason for Leaving</FormLabel>
                  <TextField
                    value={formData.reasonForLeaving || ''}
                    onChange={(e) => updateFormData('reasonForLeaving', e.target.value)}
                    placeholder="e.g., Career advancement, relocation, etc."
                    fullWidth
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={!formData.companyName || !formData.position || !formData.startDate}
            sx={{
              backgroundColor: '#212121',
              '&:hover': { backgroundColor: '#424242' }
            }}
          >
            {editingEmployment ? 'Update' : 'Add Experience'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmploymentExperience;